import { type StatusWindow, statusWindowFromPath } from "./app-routing";

export type PortalRoute =
  | { view: "requests" }
  | { view: "request"; requestId: string; step?: RequestStep }
  | { view: "status"; statusWindow: StatusWindow }
  | { view: "merge-readiness" }
  | { view: "repositories" }
  | { view: "team" };

export type RequestBoardTab = "ask" | "activity" | "board";
export type RequestStep = "request" | "plan" | "delivery" | "qa" | "merge" | "deploy";

export const REQUEST_STEPS: RequestStep[] = ["request", "plan", "delivery", "qa", "merge", "deploy"];

const PORTAL_RETURN_TO_KEY = "netraPortalReturnTo";
const REQUEST_BOARD_TAB_KEY = "netraRequestBoardTab";

function historyRecord(state: unknown): Record<string, unknown> {
  return state !== null && typeof state === "object" && !Array.isArray(state)
    ? state as Record<string, unknown>
    : {};
}

export function portalNavigationState(state: unknown, returnTo: string): Record<string, unknown> {
  return { ...historyRecord(state), [PORTAL_RETURN_TO_KEY]: returnTo };
}

export function portalReturnTo(state: unknown) {
  const returnTo = historyRecord(state)[PORTAL_RETURN_TO_KEY];
  return typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//")
    ? returnTo
    : null;
}

export function requestBoardTabFromState(state: unknown): RequestBoardTab {
  const tab = historyRecord(state)[REQUEST_BOARD_TAB_KEY];
  return tab === "activity" || tab === "board" ? tab : "ask";
}

export function requestBoardHistoryState(state: unknown, tab: RequestBoardTab): Record<string, unknown> {
  return { ...historyRecord(state), [REQUEST_BOARD_TAB_KEY]: tab };
}

function decode(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Maps requester workflow destinations embedded in the unified Netra shell. */
export function parsePortalRoute(pathname: string): PortalRoute | null {
  const parts = pathname.split("/").filter(Boolean).map(decode);
  if (parts.length === 1 && parts[0] === "requests") return { view: "requests" };
  if (parts.length === 2 && parts[0] === "requests" && parts[1]) {
    return { view: "request", requestId: parts[1] };
  }
  if (
    parts.length === 3
    && parts[0] === "requests"
    && parts[1]
    && REQUEST_STEPS.includes(parts[2] as RequestStep)
  ) {
    return { view: "request", requestId: parts[1], step: parts[2] as RequestStep };
  }
  if ((parts.length === 1 && parts[0] === "merge-readiness") || (parts[0] === "work" && parts[1] === "merge")) {
    return { view: "merge-readiness" };
  }
  if ((parts.length === 2 && parts[0] === "portal" && parts[1] === "repositories") || (parts[0] === "settings" && parts[1] === "repositories")) {
    return { view: "repositories" };
  }
  // Members see service status without the rest of the control room. The page
  // carries no request content, and it is the same component and the same URLs
  // the administrator shell serves, so a link works for either reader.
  if (parts[0] === "status" && parts.length <= 2) {
    return { view: "status", statusWindow: statusWindowFromPath(parts[1]) };
  }
  if (parts.length === 1 && parts[0] === "inbox") return { view: "requests" };
  if ((parts.length === 1 && parts[0] === "team") || (parts[0] === "settings" && parts[1] === "team")) return { view: "team" };
  return null;
}

export function portalRequestPath(requestId: string, step?: RequestStep) {
  const base = `/requests/${encodeURIComponent(requestId)}`;
  return step ? `${base}/${step}` : base;
}

export function isPortalPath(pathname: string) {
  return ["requests", "inbox", "team", "status"].includes(pathname.split("/").filter(Boolean)[0] ?? "");
}
